import type Board from '@highcharts/dashboards/es-modules/Dashboards/Board';
import {
    compileDataConnectors,
    describeComponent,
    serializeDashboardLayout,
} from '@h20/contracts/utils/dashboard';
import { isProviderConnector } from '@h20/contracts/types/api/dashboard/DashboardLayout';
import type {
    DashboardLayoutBlob,
    DashboardLayoutComponent,
} from '@h20/contracts/types/api/dashboard/DashboardLayout';
import { isDashboardQueryKey } from '@h20/contracts/types/api/dashboard/DashboardQueryCatalog';
import type { DashboardQueryKey } from '@h20/contracts/types/api/dashboard/DashboardQueryCatalog';
import { isRecord } from '@h20/contracts/utils/object';
import { Dashboards } from '../dashboards-core';
import {
    DASHBOARD_COMPONENT_KEYS,
    isDashboardComponentKey,
} from '../widgets/dashboard-component-types';
import type { DashboardComponentKey } from '../widgets/dashboard-component-types';
import { registeredWidgetKeys } from '../widgets/dashboard-widget-registry';
import type { DashboardLayout } from '../dashboard.types';

/** A layout's geometry and its placed components, as the renderer takes them. */
type RenderableGui = NonNullable<DashboardLayout['gui']>;
type RenderableComponent = NonNullable<DashboardLayout['components']>[number];

/** A component held to this build's registries — the component analogue of `DashboardProviderConnector`. */
type VerifiedComponent = DashboardLayoutComponent & {
    type: DashboardComponentKey;
    queryKey?: DashboardQueryKey;
    queryParams?: Record<string, string | boolean>;
    connector?: { id: string };
};

/** What an authored board serializes to, and what the translation could not carry. */
export type SerializedDashboard = {
    /** The persistence shape: the board's gui geometry, its placed components, and the pool their bindings compile to. */
    layout: DashboardLayoutBlob;
    /** Empty means the board translated cleanly. Anything here is the author's to fix before saving. */
    problems: string[];
};

export type DeserializedDashboard = {
    layout: DashboardLayout;
    problems: string[];
};

/** Reads back what the author arranged. */
export function serializeBoard(board: Board): SerializedDashboard {
    const serialized = serializeDashboardLayout(board.getOptions());

    const compiled = compileDataConnectors(withoutSettingsFields(serialized.layout), {
        componentTypes: DASHBOARD_COMPONENT_KEYS,
        widgetKeys: registeredWidgetKeys(),
    });

    return { layout: compiled.layout, problems: [...serialized.problems, ...compiled.problems] };
}

/** Drops each cell's `editableOptions`, which describe its settings panel rather than the board. */
function withoutSettingsFields(layout: DashboardLayoutBlob): DashboardLayoutBlob {
    const components = layout.components;
    if (components === undefined) {
        return layout;
    }

    return {
        ...layout,
        components: components.map((component) => {
            if (component['editableOptions'] === undefined) {
                return component;
            }
            const stored = { ...component };
            delete stored['editableOptions'];
            return stored;
        }),
    };
}

/** A stored layout as a board the author can work on. */
export function deserializeBoard(blob: DashboardLayoutBlob): DeserializedDashboard {
    const compiled = compileDataConnectors(structuredClone(blob));
    const layout = compiled.layout;
    const problems = [...compiled.problems];

    const storedGui = toRenderableGui(layout.gui);
    const storedComponents = layout.components ?? [];

    if (storedGui === undefined && storedComponents.length > 0) {
        problems.push('the stored board declares no layout, so nothing it placed could be opened');
    }

    return {
        layout: {
            gui: storedGui ?? emptyCanvas(),
            components:
                storedGui === undefined
                    ? []
                    : storedComponents.flatMap(
                          (component) => toRenderableComponent(component, problems) ?? []
                      ),
            dataPool: {
                connectors: (layout.dataPool?.connectors ?? []).filter(isProviderConnector),
            },
        },
        problems,
    };
}

function emptyCanvas(): RenderableGui {
    return { layouts: [{ id: `pulse-dashboard-layout-${Dashboards.uniqueKey()}`, rows: [] }] };
}

function toRenderableGui(storedGui: unknown): RenderableGui | undefined {
    if (!isRecord(storedGui) || !Array.isArray(storedGui.layouts)) {
        return undefined;
    }

    const layouts = storedGui.layouts.filter(isRecord);
    return layouts.length === 0 ? undefined : { ...storedGui, layouts };
}

function toRenderableComponent(
    component: DashboardLayoutComponent,
    problems: string[]
): RenderableComponent | undefined {
    const type = component.type;
    if (type === undefined) {
        problems.push(
            `${describeComponent(component)} names no component type, so it was left out`
        );
        return undefined;
    }
    if (!isDashboardComponentKey(type)) {
        problems.push(
            `${describeComponent(component)} was left out: '${type}' is not a component type this platform renders`
        );
        return undefined;
    }

    const queryKey = component.queryKey;
    const connectorId = component.connector?.id;

    const renderable: VerifiedComponent = {
        ...component,
        type,
        queryKey: isDashboardQueryKey(queryKey) ? queryKey : undefined,
        queryParams: toEditableQueryParams(component, problems),
        connector: connectorId === undefined ? undefined : { id: connectorId },
    };
    return renderable;
}

/** The parameter values a stored widget holds, in the shape the settings inputs read and write. */
function toEditableQueryParams(
    component: DashboardLayoutComponent,
    problems: string[]
): Record<string, string | boolean> | undefined {
    const stored = component.queryParams;
    if (!isRecord(stored)) {
        return undefined;
    }

    const editable: [string, string | boolean][] = [];
    for (const [name, value] of Object.entries(stored)) {
        if (typeof value === 'string' || typeof value === 'boolean') {
            editable.push([name, value]);
        } else if (typeof value === 'number') {
            editable.push([name, String(value)]);
        } else {
            problems.push(
                `${describeComponent(component)}: the parameter '${name}' was dropped because the settings panel cannot edit its value`
            );
        }
    }

    return editable.length === 0 ? undefined : Object.fromEntries(editable);
}
